import { useState } from 'react';
import {
  apiErrorText,
  useCheckFsnbRegistryMutation,
  useListFsnbRegistryQuery,
} from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Card } from '@shared/ui/Card';
import { editionStatusLabel, editionStatusTone, fmtDate } from '../../normBaseLabels';
import {
  Alert,
  BlockTitle,
  ExternalLink,
  Grid,
  Grow,
  Lead,
  Muted,
  Note,
  Row,
  TableWrap,
} from '../styles';

/** Дополнения, вышедшие на ФГИС ЦС, и что из них у нас загружено.
 *  `activeDate` — дата приказа действующей редакции: старшие её дополнения
 *  не нужны, а младшие, но не новее загруженного, перекрыты им (архив
 *  дополнения — полный срез базы). */
export function RegistryBlock({
  canCheck,
  activeDate,
}: {
  canCheck: boolean;
  activeDate: string | null;
}) {
  const q = useListFsnbRegistryQuery();
  const [check, { isLoading }] = useCheckFsnbRegistryMutation();
  const [showAll, setShowAll] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const items = q.data?.items ?? [];
  const shown = showAll ? items : items.slice(0, 5);

  async function onCheck() {
    setError(null);
    try {
      await check().unwrap();
    } catch (e) {
      setError(apiErrorText(e, 'ФГИС ЦС не ответил. Повторите позже.'));
    }
  }

  return (
    <Card>
      <Row>
        <BlockTitle>Дополнения на ФГИС ЦС</BlockTitle>
        <Grow />
        {q.data?.source_url && (
          <ExternalLink href={q.data.source_url} target="_blank" rel="noreferrer">
            Открыть ФГИС ЦС
          </ExternalLink>
        )}
        {canCheck && (
          <Button
            type="button"
            $variant="secondary"
            $size="sm"
            disabled={isLoading}
            onClick={onCheck}
          >
            {isLoading ? 'Проверяем…' : 'Проверить сейчас'}
          </Button>
        )}
      </Row>
      <Lead>
        ОСНОВА раз в сутки смотрит открытый список дополнений и сообщает сметчикам о новом. Скачать
        архив автоматически нельзя: ФГИС ЦС закрывает скачивание проверкой «я не робот».
      </Lead>
      {error && <Alert $tone="danger">{error}</Alert>}
      {q.isLoading && <Note>Загрузка…</Note>}
      {!q.isLoading && items.length === 0 && (
        <Note>Список ещё не получен — нажмите «Проверить сейчас».</Note>
      )}
      {items.length > 0 && (
        <TableWrap>
          <Grid>
            <thead>
              <tr>
                <th>Дополнение</th>
                <th>Приказ</th>
                <th>У нас</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((i) => (
                <tr key={i.registry_id}>
                  <td>№{i.supplement ?? '—'}</td>
                  <td>
                    {i.decree_number ?? '—'}
                    {i.decree_date ? ` от ${fmtDate(i.decree_date)}` : ''}
                  </td>
                  <td>
                    {i.loaded ? (
                      <Badge $tone={editionStatusTone(i.loaded.status)}>
                        {editionStatusLabel(i.loaded.status)}
                      </Badge>
                    ) : i.newer ? (
                      <Badge $tone="warning">Вышло новое — не загружено</Badge>
                    ) : activeDate && i.decree_date && i.decree_date > activeDate ? (
                      <Muted>не нужно — загружено более новое</Muted>
                    ) : (
                      <Muted>старее действующей</Muted>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Grid>
        </TableWrap>
      )}
      {items.length > 5 && (
        <Button type="button" $variant="ghost" $size="sm" onClick={() => setShowAll((v) => !v)}>
          {showAll ? 'Свернуть' : `Показать все (${items.length})`}
        </Button>
      )}
    </Card>
  );
}
